import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";

/**
 * Header_Title_Desc Component
 * Displays a header section with:
 * - Main heading (title)
 * - Subtitle/tagline
 * - Description paragraph
 * - Optional call-to-action button
 * All centered with clean, minimalist design
 */
const HeaderTitleDesc = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    subtitle = "",
    description = "",
    buttonText = "",
    buttonLink = "#",
    component_name = "Header_Title_Desc",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localSubtitle, setLocalSubtitle] = useState(subtitle);
  const [localDescription, setLocalDescription] = useState(description);
  const [localButtonText, setLocalButtonText] = useState(buttonText);
  const [localButtonLink, setLocalButtonLink] = useState(buttonLink);

  useEffect(() => {
    setLocalTitle(title || "");
    setLocalSubtitle(subtitle || "");
    setLocalDescription(description || "");
    setLocalButtonText(buttonText || "");
    setLocalButtonLink(buttonLink || "#");
  }, [id, title, subtitle, description, buttonText, buttonLink]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      subtitle: localSubtitle,
      description: localDescription,
      buttonText: localButtonText,
      buttonLink: localButtonLink,
    });
    setIsEditing(false);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component header-title-desc-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit Header with Title & Description</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Title/Heading</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Enter main heading"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Subtitle/Tagline</label>
          <input
            className="form-control"
            value={localSubtitle}
            onChange={(e) => setLocalSubtitle(e.target.value)}
            placeholder="Enter subtitle"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description"
            rows={4}
          />
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Button Text</label>
            <input
              className="form-control"
              value={localButtonText}
              onChange={(e) => setLocalButtonText(e.target.value)}
              placeholder="Educate Me"
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label fw-bold">Button Link</label>
            <input
              className="form-control"
              value={localButtonLink}
              onChange={(e) => setLocalButtonLink(e.target.value)}
              placeholder="#"
            />
          </div>
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  return (
    <>
      {!previewMode && (
        <div className="email-component header-title-desc mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            align="center"
            style={{
              padding: "48px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            <div
              style={{
                maxWidth: "600px",
                margin: "0 auto",
                textAlign: "center",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              {/* Main Heading */}
              {localTitle && (
                <h1
                  style={{
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: primaryColor || "#333333",
                    marginBottom: "12px",
                    marginTop: "0",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    lineHeight: "1.2",
                  }}
                >
                  {localTitle}
                </h1>
              )}

              {/* Subtitle/Tagline */}
              {localSubtitle && (
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "400",
                    color: primaryColor || "#333333",
                    marginBottom: "20px",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    lineHeight: "1.4",
                  }}
                >
                  {localSubtitle}
                </div>
              )}

              {/* Description */}
              {localDescription && (
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "400",
                    color: primaryColor || "#333333",
                    lineHeight: "1.6",
                    marginBottom: "24px",
                    textAlign: "left",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  }}
                >
                  {localDescription}
                </p>
              )}

              {/* Call-to-Action Button */}
              {localButtonText && (
                <div style={{ marginTop: "24px" }}>
                  <table
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    align="center"
                    style={{
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    <tr>
                      <td
                        align="center"
                        style={{
                          borderRadius: "4px",
                          border: `1px solid ${secondaryColor || "#d4a574"}`,
                          backgroundColor: "#fafafa",
                          padding: "0",
                        }}
                      >
                        <a
                          href={localButtonLink || "#"}
                          style={{
                            display: "inline-block",
                            padding: "12px 32px",
                            fontSize: "16px",
                            fontWeight: "400",
                            color: primaryColor || "#333333",
                            textDecoration: "none",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          {localButtonText}
                        </a>
                      </td>
                    </tr>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      </table>
    </>
  );
};

export default HeaderTitleDesc;

